import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { GoogleLoginButton } from "modules/authentication/components/google-login-button";
import { CreateAccountForm } from "modules/authentication/components/create-account-form";
import { useCreateAccount } from "modules/authentication/components/use-create-account";
import { useAuth } from "modules/authentication/hooks/use-auth";
import Amount from "utils/amount";
import { computeSavings, PLUS_THRESHOLD_CLP } from "./step-five";
import { readOnboardingState } from "../types/onboarding-state";

/**
 * Final onboarding step. Renders the dynamic savings headline
 * (computed from the persisted step-5 monthly_spend, capped at $150K
 * without Carriapp Plus), the subtitle, the Google sign-in control,
 * and the email registration form below it.
 */
const StepSix = () => {
  const navigate = useNavigate();
  const { isLogged } = useAuth();

  /**
   * Hook owned by Step 6 — same shape `LoginRegisterDialog` uses, but
   * the `onAuthenticated` callback here navigates to `/store` instead
   * of closing a dialog. The hook calls `checkAuth` internally before
   * firing `onAuthenticated`, so by the time we navigate the session is
   * already valid.
   */
  const createAccountHook = useCreateAccount({
    onAuthenticated: () => {
      // No-op: the post-auth navigation is driven by the `isLogged`
      // effect below. We keep the callback wired so the hook can still
      // close any local OTP screen state cleanly.
    },
  });

  /**
   * Post-auth redirect. The hook calls `checkAuth(userData)` internally
   * and only then triggers `onAuthenticated`. By the time `isLogged`
   * flips to `true` here, the session is fully established and we can
   * safely navigate to `/store`.
   */
  useEffect(() => {
    if (isLogged) {
      navigate("/store");
    }
  }, [isLogged, navigate]);

  // Once the OTP code has been sent we collapse the Google area so the
  // user's focus shifts to the verification code input. The wrapper is
  // kept in the DOM (with `aria-hidden` + `pointer-events-none`) so the
  // collapse animates rather than unmounts.
  const isGoogleAreaCollapsed = createAccountHook.confirmMessage;

  // Lee los datos persistidos del paso 5 para mostrar el ahorro real
  // del usuario (sin tope de Carriapp Plus).
  const onboardingState = readOnboardingState();
  const monthlySpend = onboardingState?.monthly_spend ?? 0;
  const frequency = onboardingState?.purchase_frequency ?? null;
  const projection = computeSavings(monthlySpend, frequency);
  const headlineSavings = Math.min(projection.annualSavings, PLUS_THRESHOLD_CLP);

  return (
    <section
      className="flex flex-col gap-4"
      aria-label="Paso 6"
    >
      <h1 className="text-2xl font-semibold leading-tight text-foreground">
        Construyamos tu ahorro de{" "}
        <Amount amount={headlineSavings} currency="CLP" locale="es-CL" /> anual
      </h1>
      <p className="text-muted-foreground leading-relaxed">
        Crea tu cuenta y armemos tu primera lista para empezar a ahorrar
        hoy.
      </p>
      <div className="flex flex-col gap-3">
        <div
          data-testid="step-six-google-area"
          data-state={isGoogleAreaCollapsed ? "collapsed" : "expanded"}
          aria-hidden={isGoogleAreaCollapsed ? "true" : undefined}
          className={cn(
            "transition-all duration-300 ease-in-out overflow-hidden",
            isGoogleAreaCollapsed
              ? "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
              : "max-h-32 opacity-100 translate-y-0",
          )}
        >
        </div>
        <CreateAccountForm
          hook={createAccountHook}
          showGoogleButton={true}
          showSwitchToLogin={false}
          layout="onboarding"
        />
      </div>
    </section>
  );
};

export default StepSix;
